import {
  dLocal as Dlocal,
  MercadoPagoLogo,
  PayU,
  Supervielle,
} from '@increasecard/icons';
import {
  CardCollectMethod,
  CbuCollectMethod,
  CollectMethod,
  DebinCollectMethod,
  GroupedCollectMethods,
  QRCollectMethod,
  TicketCollectMethod,
} from '../types/CollectMethod';
import { PaymentMethod } from '../types/PaymentMethod';
import comafiLogo from '../components/common/assets/comafi-logo.png';
import decidirLogo from '../components/common/assets/decidir-logo.png';
import macroLogo from '../components/common/assets/macro-logo.png';
import { Box } from '../components/common/Box';

export function isCard(cm: CollectMethod): cm is CardCollectMethod {
  return cm.payment_categories.includes('card');
}

export function isCbu(cm: CollectMethod): cm is CbuCollectMethod {
  return cm.payment_categories.includes('cbu');
}

export function isTicket(cm: CollectMethod): cm is TicketCollectMethod {
  return cm.payment_categories.includes('ticket');
}

export function isDebin(cm: CollectMethod): cm is DebinCollectMethod {
  return cm.payment_categories.includes('debin');
}

export function isQr(cm: CollectMethod): cm is QRCollectMethod {
  return cm.payment_categories.includes('qr');
}

export function isTransfer(cm: CollectMethod): cm is QRCollectMethod {
  return cm.payment_categories.includes('transfer');
}

export function isDefault(cm: CollectMethod): boolean {
  return cm.default;
}

export function groupCollectMethods(
  cmList: CollectMethod[]
): GroupedCollectMethods {
  return {
    card: cmList.filter(isCard),
    cbu: cmList.filter(isCbu),
    ticket: cmList.filter(isTicket),
    qr: cmList.filter(isDebin),
    debin: cmList.filter(isQr),
    transfer: cmList.filter(isTransfer),
  };
}
export function hasCollectMethods(
  groupedCM: GroupedCollectMethods | undefined
): groupedCM is GroupedCollectMethods {
  if (!groupedCM) {
    return false;
  }

  return Object.values(groupedCM).flat().length > 0;
}

export function filterPaymentMethodsByCollectMethods(
  groupedCollectMethods: GroupedCollectMethods | undefined,
  paymentMethods: PaymentMethod[]
): PaymentMethod[] {
  if (!groupedCollectMethods) {
    return paymentMethods;
  }
  const collectMethodIds = Object.values(groupedCollectMethods)
    .flat(1)
    .map((cm) => cm.id);

  return paymentMethods.filter((paymentMethod) =>
    collectMethodIds.includes(paymentMethod.collect_method_id)
  );
}

export function getCollectMethodsNames(
  groupedCollectMethods: GroupedCollectMethods | undefined
): string {
  if (!groupedCollectMethods) {
    return '';
  }

  return Object.values(groupedCollectMethods)
    .flat()
    .map((cm) => cm.name)
    .join(', ');
}

function ComafiLogo(): JSX.Element {
  // Import result is the URL of your image
  return <img src={comafiLogo} width="160px" alt="Logo" />;
}

function DecidirLogo(): JSX.Element {
  // Import result is the URL of your image
  return <img src={decidirLogo} height="30px" alt="Logo" />;
}

function SupervielleLogo(): JSX.Element {
  return <Supervielle height="40px" />;
}

function MacroLogo(): JSX.Element {
  // Import result is the URL of your image
  return <img src={macroLogo} height="30px" alt="Logo" />;
}

function DlocalLogo(): JSX.Element {
  return <Dlocal height="30px" />;
}

function Mercadopago(): JSX.Element {
  return <MercadoPagoLogo height="30px" />;
}

function PagoticLogo(): JSX.Element {
  return (
    <Box
      height="30px"
      width="80px"
      background="black"
      color="var(--color-gray-white)"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      PAGOTIC
    </Box>
  );
}

export const CollectMethodsLogos: Record<
  Exclude<CollectMethod['type'], 'mercadopago'>,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  pagotic: PagoticLogo,
  macro: MacroLogo,
  mercadopago_connect: Mercadopago, // Agregator
  decidir: DecidirLogo,
  comafi: ComafiLogo,
  // Deprecated,
  dlocal: DlocalLogo,
  dlocal_cross: DlocalLogo,
  payments_os: PayU,
  supervielle: SupervielleLogo,
};
